import React, { useState, useEffect, useRef, useCallback } from "react";
import { useMobileDetection } from "../../utils/deviceDetection";
import TutorialTooltip from "./TutorialTooltip";

const DESKTOP_TUTORIAL_STEPS = [
  {
    id: "round-tracker",
    title: "Round Tracker",
    description: "This shows the current round and your turn information. You have 40 seconds per action (3 actions = 120 seconds, 4 actions = 160 seconds). You can end your turn early by clicking 'End Turn'. Dividends are paid during the End of Round phase on even rounds (2, 4, 6, 8, 10).",
    selector: "[data-tutorial='round-tracker']",
    position: "bottom"
  },
  {
    id: "game-phases",
    title: "Game Phases",
    description: "The game progresses through four phases: 📊 Stock Options → 📰 Events → 💼 Trading → 🎲 End of Round. During End of Round, stock prices update, delayed manipulators take effect, and dividends are paid (even rounds only). The active phase is highlighted.",
    selector: "[data-tutorial='game-phases']",
    position: "bottom"
  },
  {
    id: "game-log",
    title: "Game Log",
    description: "In the chat area, switch to the 📊 Game Log tab to view the full game log (market news and player updates). Key events also appear as notifications during play.",
    selector: "[data-tutorial='game-log']",
    position: "left"
  },
  {
    id: "player-details",
    title: "Player Information & Turn Order",
    description: "See all players' cash, net worth, and actions. The numbers (1, 2, 3...) show the turn order - players take turns in this sequence. The player whose turn it is will be highlighted with a green border and pulsing animation. Watch for the highlighted player to know when it's your turn!",
    selector: "[data-tutorial='player-details']",
    position: "left"
  },
  {
    id: "events",
    title: "Events Section",
    description: "Market events appear here. Some events create 'bubbles' that can pop and affect index prices. You can also draw action cards from the deck.",
    selector: "[data-tutorial='events']",
    position: "right"
  },
  {
    id: "regular-indexes",
    title: "Regular Index Cards",
    description: "These 4 indexes represent market sectors: Tech 💻, Finance 🏦, Industrial 🏭, and Health & Science 🧬. Their prices change based on events that affect the market. You can buy or sell indexes to diversify your portfolio and benefit from sector-wide price movements.",
    selector: "[data-tutorial='regular-index']",
    position: "bottom"
  },
  {
    id: "stocks",
    title: "Stock Cards",
    description: "Company stocks. Price = Base + Index 📈 + (Growth × Shares). Prices use round-start ownership and update next round. Net worth 💎 = cash + sell value (sell = price − growth). Dividends 🪙 on even rounds (2, 4, 6, 8). You can sell multiple per turn; you cannot buy and sell the same stock in one turn.",
    selector: "[data-tutorial='stocks']",
    position: "bottom"
  },
  {
    id: "your-hand",
    title: "Your Hand",
    description: "Your action cards. Select a card, then choose a target (stock, index, or ownership token) to apply its effect. You can draw new cards when it's your turn (max 6 cards in hand). You can also Discard & Redraw (1-3 cards, once per turn) to cycle out cards you can't use. Cards include market manipulation (FOMO/Sinking Ship), Insider Trading, Fake News, Trading Halt, Rug Pull, and Forced Sale.",
    selector: "[data-tutorial='your-hand']",
    position: "top"
  }
];

const MOBILE_TUTORIAL_STEPS = [
  {
    id: "mobile-header",
    title: "Game Header & Turn Order",
    description: "This shows the current round, game phase (📊→📰→💼→🎲), turn timer, and whose turn it is. The ▶ icon shows the current player's turn. Players take turns in order (shown in the menu). You have 40 seconds per action (3 actions = 120 seconds, 4 actions = 160 seconds). The menu button (☰) opens options, and the chat button opens the chat.",
    selector: "[data-tutorial='mobile-header']",
    position: "bottom"
  },
  {
    id: "mobile-menu",
    title: "Menu Button",
    description: "Tap here to access the menu with game actions, active events, player info (including full turn order), tutorial, and game rules. The menu shows all players in turn order with the current turn highlighted.",
    selector: "[data-tutorial='mobile-menu']",
    position: "bottom"
  },
  {
    id: "regular-indexes",
    title: "Regular Index Cards",
    description: "These 4 indexes represent market sectors: Tech 💻, Finance 🏦, Industrial 🏭, and Health & Science 🧬. Their prices change based on events that affect the market. Tap a card to buy or sell indexes and diversify your portfolio.",
    selector: "[data-tutorial='regular-index']",
    position: "bottom"
  },
  {
    id: "stocks",
    title: "Stock Cards",
    description: "Company stocks. Price = Base + Index + (Growth × Shares). Tap to buy or sell. Net worth = cash + sell value. Dividends on even rounds.",
    selector: "[data-tutorial='stocks']",
    position: "bottom"
  },
  {
    id: "your-hand",
    title: "Your Hand",
    description: "Swipe up to see your action cards. Select a card, then tap a stock or index to apply its effect. You can also draw new cards here.",
    selector: "[data-tutorial='your-hand']",
    position: "top"
  }
];

const STORAGE_KEY = "game_tutorial_completed";

const GameTutorial = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightBox, setHighlightBox] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const overlayRef = useRef(null);
  const highlightRef = useRef(null);

  // Detect mobile viewport
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const TUTORIAL_STEPS = isMobile ? MOBILE_TUTORIAL_STEPS : DESKTOP_TUTORIAL_STEPS;

  const calculateBoundingBox = useCallback((elements) => {
    if (elements.length === 0) return null;
    
      let minTop = Infinity;
      let minLeft = Infinity;
      let maxBottom = -Infinity;
      let maxRight = -Infinity;

      elements.forEach(element => {
        const rect = element.getBoundingClientRect();
        minTop = Math.min(minTop, rect.top);
        minLeft = Math.min(minLeft, rect.left);
        maxBottom = Math.max(maxBottom, rect.bottom);
        maxRight = Math.max(maxRight, rect.right);
      });

    return {
        top: minTop,
        left: minLeft,
        width: maxRight - minLeft,
      height: maxBottom - minTop
    };
  }, []);

  const updateHighlight = useCallback((keepPrevious = false) => {
    if (!isOpen || currentStep >= TUTORIAL_STEPS.length) return;

    const step = TUTORIAL_STEPS[currentStep];
    const elements = document.querySelectorAll(step.selector);

    if (elements.length > 0) {
      const boundingBox = calculateBoundingBox(elements);
      if (boundingBox) {
        setHighlightBox({
          ...boundingBox,
        position: step.position
      });

      // Scroll the first element into view
      elements[0].scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center"
      });
      }
    } else if (!keepPrevious) {
      console.warn(`[Tutorial] Element not found for step ${currentStep}: ${step.selector}`);
    }
  }, [isOpen, currentStep, TUTORIAL_STEPS, calculateBoundingBox]);

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      // Initialize highlight for step 0 when tutorial opens
      const timer = setTimeout(() => {
        const step = TUTORIAL_STEPS[0];
        const elements = document.querySelectorAll(step.selector);
        if (elements.length > 0) {
          const boundingBox = calculateBoundingBox(elements);
          if (boundingBox) {
          setHighlightBox({
              ...boundingBox,
            position: step.position
          });
          elements[0].scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "center"
          });
          }
        }
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setHighlightBox(null);
      setCurrentStep(0);
    }
  }, [isOpen, TUTORIAL_STEPS, calculateBoundingBox]); // Only reset when isOpen changes

  useEffect(() => {
    if (isOpen && currentStep < TUTORIAL_STEPS.length) {
      let retryCount = 0;
      const maxRetries = 20;
      let timeoutId = null;
      
      const tryUpdateHighlight = () => {
        const step = TUTORIAL_STEPS[currentStep];
        const elements = document.querySelectorAll(step.selector);
        
        if (elements.length > 0) {
          updateHighlight(false);
        } else if (retryCount < maxRetries) {
          retryCount++;
          timeoutId = setTimeout(tryUpdateHighlight, 100);
        } else {
          console.warn(`[Tutorial] Failed to find element after ${maxRetries} retries: ${step.selector}`);
          updateHighlight(true);
        }
      };
      
      const handleResize = () => updateHighlight(true);
      const handleScroll = () => updateHighlight(true);
      
      const initialTimer = setTimeout(tryUpdateHighlight, 150);
      window.addEventListener("resize", handleResize);
      window.addEventListener("scroll", handleScroll);
      return () => {
        clearTimeout(initialTimer);
        if (timeoutId) clearTimeout(timeoutId);
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("scroll", handleScroll);
      };
    }
  }, [isOpen, currentStep, updateHighlight]);

  const handleComplete = useCallback(() => {
    setHighlightBox(null);
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  const handleNext = useCallback(() => {
    console.log('handleNext called, currentStep:', currentStep);
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      const nextStep = currentStep + 1;
      console.log('Moving to step:', nextStep);
      setCurrentStep(nextStep);
    } else {
      console.log('Completing tutorial');
      handleComplete();
    }
  }, [currentStep, handleComplete]);

  const handleSkip = useCallback(() => {
    handleComplete();
  }, [handleComplete]);

  if (!isOpen) {
    return null;
  }

  if (!highlightBox && currentStep < TUTORIAL_STEPS.length) {
    return null;
  }

  const step = TUTORIAL_STEPS[currentStep];

  return (
    <>
      <div
        ref={overlayRef}
        className="tutorial-overlay"
        onClick={(e) => {
          if (e.target === overlayRef.current) {
            handleSkip();
          }
        }}
      >
        <div
          ref={highlightRef}
          className="tutorial-highlight"
          style={{
            top: `${highlightBox.top}px`,
            left: `${highlightBox.left}px`,
            width: `${highlightBox.width}px`,
            height: `${highlightBox.height}px`,
          }}
        />
      </div>

      <TutorialTooltip
        step={step}
        currentStep={currentStep}
        totalSteps={TUTORIAL_STEPS.length}
        highlightBox={highlightBox}
        onNext={handleNext}
        onSkip={handleSkip}
      />
    </>
  );
};

export default GameTutorial;

